import React, { useState, useRef } from "react";
import axios from "axios";
import PageTitle from "./PageTitle";
import { toggleMenu } from "../utils/helpers";
import { FaRegFileAlt } from "react-icons/fa";
import styles from "../Styles/modules/appraisal.module.css";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

const BulkFileUploader = ({
  title,
  description,
  uploadUrl,
  fileTypes = ["xls", "xlsx"],
  buttonText = "Upload",
  onSuccess,
  isDateSelected = true,
  /** When set, appended to multipart body as `locationSync` ("true" | "false"). */
  locationSync,
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showFile, setShowFile] = useState(false);

  const fileInputRef = useRef(null);
  const fakeProgressRef = useRef(null);
  const accessToken = Cookies.get("accessToken");

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else {
      setDragActive(false);
    }
  };

  const validateFile = (uploadedFile) => {
    const ext = uploadedFile.name.split(".").pop().toLowerCase();
    return fileTypes.includes(ext);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const uploadedFile = e.dataTransfer.files[0];

      if (validateFile(uploadedFile)) {
        setFile(uploadedFile);
        setShowFile(true);
        setProgress(0);
      } else {
        toast.error(`Please upload only ${fileTypes.join(", ")} files.`);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const uploadedFile = e.target.files[0];
      if (validateFile(uploadedFile)) {
        setShowFile(false);
        setTimeout(() => {
          setFile(uploadedFile);
          setShowFile(true);
          setProgress(0);
        }, 200);
      } else {
        toast.error(`Please upload only ${fileTypes.join(", ")} files.`);
      }
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please upload a file first!");
      return;
    }

    if (!uploadUrl) {
      toast.error("Please select date first!");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    if (typeof locationSync === "boolean") {
      formData.append("locationSync", locationSync ? "true" : "false");
    }

    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded * 100) / e.total);
          setProgress(percent);
        },
      });

      setProgress(100);

      if (onSuccess) {
        onSuccess(response.data);
      } else {
        toast.success(response.data.message || "File uploaded successfully!");
      }

      setTimeout(() => {
        setFile(null);
        setShowFile(false);
        setProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }, 400);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowFile(false);
    setTimeout(() => {
      setFile(null);
      setProgress(0);
      fileInputRef.current.value = "";
    }, 300);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div
      className={`${styles.fileUploaderSection} d-flex flex-column justify-content-center`}
    >
      <div
        className={`${styles.fileUploader} d-flex justify-content-center`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept={fileTypes.map((t) => `.${t}`).join(",")}
          onChange={handleChange}
          style={{ display: "none" }}
        />

        <label
          className="dropzone-label"
          onClick={() => fileInputRef.current.click()}
        >
          <div className={`${styles.uploadIconWrapper}`}>
            <img
              width="45"
              height="45"
              src="../../../assets/xlLogo.png"
              alt="file-upload"
            />
          </div>

          <div className="dropzone-text">
            <p className="fs-5 mb-0 text-dark">Drop Excel files here</p>
            <p className="fs-6 text-muted">or click to browse</p>
          </div>
        </label>
      </div>

      <div
        className={`${styles.selectedFileWrapper} ${
          showFile ? styles.show : ""
        } mt-2`}
      >
        <div className={`${styles.selectedFile} position-relative`}>
          <div
            className={`${styles.progressFill}`}
            style={{ width: `${progress}%` }}
          />
          <div className="w-100 d-flex align-items-center gap-3">
            <FaRegFileAlt size={26} className="file-icon" color="#059669" />
            <div className={styles.fileInfo}>
              <span className="file-name">{file?.name}</span>
              <span className={styles.fileSize}>
                {formatFileSize(file?.size)}
              </span>
            </div>
          </div>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className={`${styles.fileDeleteIcon}`}
            onClick={handleCancel}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
            />
          </svg>
        </div>

        {file && (
          <div
            className="buttons-container d-flex justify-content-end gap-2"
            style={{ marginTop: "20px" }}
          >
            <button
              type="button"
              disabled={loading || !isDateSelected}
              onClick={handleSubmit}
              className={`${styles.uploadBtn} ${loading ? "disabled" : ""}`}
            >
              Upload
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkFileUploader;
